import { SimpleTextAreaControl } from "@/components/controls/SimpleSelectorControl";
import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";
import { useToken } from "@dexteel/mesf-core";
import {
	Button,
	Card,
	CardContent,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	Snackbar,
	TextField,
	Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { createStyles, makeStyles } from "@mui/styles";
import Alert from "@mui/lab/Alert";
import moment from "moment";
import { type SyntheticEvent, useCallback, useEffect, useState } from "react";

const useStyles = makeStyles((theme) =>
	createStyles({
		root: {
			"& .input-group-text": {
				display: "none",
			},
			"& .form-control": {
				height: "120px !important",
			},
		},
		modalTitle: {
			backgroundColor: "#ced2cc",
			color: "#495057",
			textAlign: "center",
		},
		modalWarning: {
			color: "white",
			backgroundColor: "#dc3545",
			textAlign: "center",
		},
		messageTitle: {
			fontSize: 15,
			fontWeight: "bold",
			color: "#0000008A",
		},
	}),
);

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	saveRow: Function;
};

export const FirstCommentDialog = ({ show, onHide, saveRow }: Props) => {
	const classes = useStyles();
	const { getUserName } = useToken();
	const [editCommentSuccess, setEditCommentSuccess] = useState(false);
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("sm");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const {
		state: { assetLOTOComments, LOTONameToLink, assetSearchName },
		actions: { setAssetLOTOComments, setOpenAddCommentDialog },
	} = useAssetDetailsContext();
	//states
	const [commentInput, setCommentInput] = useState<string>("");
	//functions

	const appendComment = (newComment: string) => {
		const now = moment().format("DD/MM/YYYY HH:mm");
		let newValue = "";

		if (
			assetLOTOComments === "" ||
			assetLOTOComments === null ||
			assetLOTOComments === "null"
		) {
			newValue = getUserName() + " - " + now + " - " + newComment;
		} else {
			newValue =
				assetLOTOComments +
				"\n" +
				getUserName() +
				" - " +
				now +
				" - " +
				newComment;
		}

		if (newComment != "") {
			setCommentInput("");
			setAssetLOTOComments(newValue);
		}
	};

	const addNewComment = () => {
		appendComment(commentInput);
	};

	const onKeyDown = (e: any) => {
		if (e.key === "Enter") {
			appendComment(e.target.value);
			e.preventDefault();
		}
	};

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setEditCommentSuccess(false);
	};

	const handleCancel = () => {
		onHide(false);
		setCommentInput("");
	};

	const handleSaveBtn = useCallback(async () => {
		setIsSubmitLoading(true);
		setAssetLOTOComments("");
		setCommentInput("");
		await saveRow();
		setOpenAddCommentDialog(false);
		setIsSubmitLoading(false);
		onHide(true);
	}, [saveRow]);

	const changeComments = (value: string) => {
		setAssetLOTOComments(value);
	};

	useEffect(() => {
		if (show) {
			setAssetLOTOComments("");
			setCommentInput("");
			setIsSubmitLoading(false);
		}
	}, [show]);

	return (
		<>
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
			>
				<DialogTitle style={{ padding: "10px 24px 10px" }}>
					TITLE: {LOTONameToLink}
				</DialogTitle>
				<div style={{ padding: "0px 24px " }}>
					<p style={{ marginBottom: "0px" }}>ASSET: {assetSearchName}</p>
				</div>

				<DialogContent dividers style={{ padding: "20px" }}>
					<Card
						variant="outlined"
						style={{
							backgroundColor: "#E9F2F8",
							minHeight: "180px",
							marginBottom: "20px",
						}}
						className={classes.root}
					>
						<CardContent>
							<Typography className={classes.messageTitle}>
								Comments:
							</Typography>
							<SimpleTextAreaControl
								value={assetLOTOComments}
								onChange={changeComments}
								disabled
								showColor={false}
								styleText={{
									border: "none",
									backgroundColor: "#E9F2F8",
									fontSize: 16,
								}}
							/>
						</CardContent>
					</Card>

					<Grid2 container spacing={2} alignItems="center">
						<Grid2 size={{ md: 9, xs: 12 }}>
							<TextField
								variant="outlined"
								margin="dense"
								multiline
								maxRows={3}
								value={commentInput}
								onChange={(e) => setCommentInput(e.target.value)}
								label="Comment"
								fullWidth
								onKeyDown={onKeyDown}
								autoComplete="off"
							/>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								variant="contained"
								color="primary"
								onClick={addNewComment}
								style={{ height: "40px", marginTop: 8 }}
							>
								Add comment
							</Button>
						</Grid2>
					</Grid2>
				</DialogContent>

				<DialogActions style={{ padding: "16px 24px" }}>
					<Button
						variant="contained"
						color="inherit"
						onClick={handleCancel}
						style={{ minWidth: 100 }}
					>
						Cancel
					</Button>
					<Button
						startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
						disabled={isSubmitLoading}
						variant="contained"
						color="primary"
						onClick={handleSaveBtn}
						style={{ minWidth: 100 }}
					>
						Add link
					</Button>
				</DialogActions>
			</Dialog>
			<Snackbar
				open={editCommentSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="info" onClose={handleClose}>
					Comment edited
				</Alert>
			</Snackbar>
		</>
	);
};
